import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);
  // Set when login hit the single-session collision — shows the takeover
  // confirmation prompt instead of silently replacing the other session.
  const [takeoverPrompt, setTakeoverPrompt] = useState(false);

  // Surface the reason if we landed here after a forced sign-out
  useEffect(() => {
    try {
      const reason = sessionStorage.getItem("session_ended_reason");
      if (reason) {
        setNotice(reason);
        sessionStorage.removeItem("session_ended_reason");
      }
    } catch (e) {
      // sessionStorage may be unavailable (private mode, locked storage). Non-fatal.
      console.warn("[Login] could not read session_ended_reason", e);
    }
  }, []);

  if (user) return <Navigate to="/" replace />;

  // User confirmed "Continue here and end the previous session?" — retry the
  // login with takeover:true. Hardened: a 401 means the password changed (or
  // was mistyped) → plain invalid-credentials error; a repeat 409 (race with
  // a still-alive old tab on a legacy backend build) gets ONE force-logout
  // fallback retry, then a clear message. Never loops.
  const confirmTakeover = async () => {
    setError("");
    setLoading(true);
    try {
      await login(email, password, { takeover: true });
      navigate("/", { replace: true });
    } catch (e2) {
      const s2 = e2?.response?.status;
      const timedOut = e2?.code === "ECONNABORTED" || (!s2 && !e2?.response?.status);
      if (s2 === 401) {
        setTakeoverPrompt(false);
        setError("Invalid email or password.");
      } else if (s2 === 409) {
        try {
          await api.post("/auth/force-logout", { email, password }, { timeout: 25000 });
          await login(email, password, { takeover: true });
          navigate("/", { replace: true });
          return;
        } catch (e3) {
          setTakeoverPrompt(false);
          setError(
            e3?.response?.status === 401
              ? "Invalid email or password."
              : "Another device is actively using this account. Close it, or try again in a minute."
          );
        }
      } else if (timedOut) {
        // Keep the prompt open so the user can hit "Continue here" again —
        // a stalled backend used to leave the button on "Signing in…" forever.
        setError("The server is taking too long to respond. Please try again in a moment.");
      } else {
        setTakeoverPrompt(false);
        setError(formatApiErrorDetail(e2?.response?.data?.detail) || "Could not take over the session. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    setTakeoverPrompt(false);
    setLoading(true);
    try {
      await login(email, password);
      navigate("/", { replace: true });
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      // 409 = single-session collision. Credentials were valid (invalid ones
      // return 401 before the session check) — ask the user to confirm the
      // takeover instead of silently ending the other session.
      if (status === 409) {
        setTakeoverPrompt(true);
        return;
      }
      let msg = formatApiErrorDetail(detail);
      if (status === 401) {
        msg = msg || "Invalid email or password.";
      } else if (!status) {
        msg = "Could not reach the server. Check your connection and try again.";
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 relative">
      {/* Floating brand bar — SBT (left) + Martech (right), no mission text */}
      <header
        data-testid="brand-bar"
        className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-6 lg:px-10 py-5 pointer-events-none"
      >
        <img
          src="https://customer-assets.emergentagent.com/job_lead-exclusion-check/artifacts/lcmlqwgq_SBT%20logo.jpg"
          alt="SBT Research"
          data-testid="sbt-logo"
          className="h-12 lg:h-14 w-auto object-contain pointer-events-auto select-none"
        />
        <img
          src="https://customer-assets.emergentagent.com/job_lead-exclusion-check/artifacts/12jtzz2h_martech%20logo.png"
          alt="Martech Metrix"
          data-testid="martech-logo"
          className="h-12 lg:h-14 w-auto object-contain pointer-events-auto select-none"
        />
      </header>

      {/* Subtle © mark, bottom-right, white text via mix-blend-difference */}
      <div
        data-testid="login-footer-mark"
        className="absolute bottom-3 right-4 z-30 text-[10px] font-mono tracking-wider text-white/80 mix-blend-difference pointer-events-none select-none"
      >
        © {new Date().getFullYear()} SBT Research · Martech Metrix
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-8 pt-28 lg:pt-32">
        <div className="w-full max-w-sm">
          <div className="meta-label mb-4">Authenticated Access</div>
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="text-primary" size={20} />
            <span className="font-display font-bold text-lg">Exclusion<span className="text-primary">.</span></span>
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight mb-2">Sign in</h1>
          <p className="text-sm text-muted-foreground mb-8">
            Master &amp; campaign-level exclusion checks for lead-ops teams.
          </p>
          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider">Email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 h-10 rounded-sm font-mono"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider">Password</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 h-10 rounded-sm font-mono"
                required
              />
            </div>
            {notice && (
              <div data-testid="login-notice" className="text-sm badge-warning px-3 py-2 rounded-sm">
                {notice}
              </div>
            )}
            {takeoverPrompt && (
              <div data-testid="takeover-prompt" className="badge-warning px-3 py-3 rounded-sm space-y-2">
                <div className="text-sm">
                  You have an active session on another device. Continue here and end the previous session?
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    data-testid="takeover-confirm-button"
                    disabled={loading}
                    onClick={confirmTakeover}
                    className="h-8 rounded-sm text-xs bg-primary hover:bg-[#00227A] text-primary-foreground"
                  >
                    {loading ? "Signing in…" : "Continue here"}
                  </Button>
                  <Button
                    type="button"
                    data-testid="takeover-cancel-button"
                    variant="outline"
                    disabled={loading}
                    onClick={() => setTakeoverPrompt(false)}
                    className="h-8 rounded-sm text-xs"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
            {error && (
              <div data-testid="login-error" className="text-sm badge-error px-3 py-2 rounded-sm">{error}</div>
            )}
            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full h-10 rounded-sm bg-primary hover:bg-[#00227A] text-primary-foreground"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </div>
      </div>

      {/* Visual — full-bleed WTC image, rendered at native quality */}
      <div className="hidden lg:block relative overflow-hidden border-l border-border">
        <img
          src="https://customer-assets.emergentagent.com/job_lead-exclusion-check/artifacts/gusw7hdu_wtc.jpg"
          alt="World Trade Center, Pune"
          data-testid="login-visual-image"
          loading="eager"
          decoding="async"
          fetchPriority="high"
          style={{ imageRendering: "auto", WebkitImageSmoothing: "high" }}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-10 text-white" data-testid="login-mission-block">
          <div className="meta-label !text-white/70 mb-3">Mission Control</div>
          <h2 className="font-display text-3xl font-bold leading-tight max-w-md">
            Stop bad leads before they hit your campaigns.
          </h2>
          <p className="text-sm text-white/80 mt-4 max-w-md">
            Centralised exclusion across emails, domains and entire campaign scopes.
          </p>
        </div>
      </div>
    </div>
  );
}
